import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import PageShell from "../components/PageShell";
import WorkLogos from "../components/WorkLogos";
import { homeServices } from "../data/siteData";

function getServiceHeading(service) {
  if (service.accent === "B") {
    return (
      <>
        <span className="mr-1.5 inline-block align-middle font-scriptB text-[50px] leading-none text-white md:text-[70px]">
          B
        </span>
        randing
        <br />& Logo
        <br />
        Design
      </>
    );
  }

  if (service.accent === "G") {
    return (
      <>
        <span className="inline-block align-middle font-scriptA text-[50px] leading-none text-white md:text-[70px]">
          G
        </span>
        raphic
        <br />
        Design
      </>
    );
  }

  return (
    <>
      UI/UX
      <br />
      <span className="inline-block align-middle font-mono text-[50px] font-light leading-none text-white md:text-[70px]">
        D
      </span>
      esign
    </>
  );
}

function getRevealClasses(isVisible, direction, delayed = false) {
  const hiddenState =
    direction === "left"
      ? "-translate-x-[72px] opacity-0"
      : "translate-x-[72px] opacity-0";

  return [
    "transform-gpu transition-[transform,opacity] duration-[760ms] ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:translate-x-0 motion-reduce:opacity-100 motion-reduce:transition-none",
    isVisible ? "translate-x-0 opacity-100" : hiddenState,
    delayed ? (isVisible ? "delay-[120ms]" : "delay-0") : "",
  ]
    .filter(Boolean)
    .join(" ");
}

export default function HomePage() {
  const serviceRowsRef = useRef([]);
  const [visibleServiceRows, setVisibleServiceRows] = useState({});

  useEffect(() => {
    const serviceRows = serviceRowsRef.current.filter(Boolean);

    if (!serviceRows.length) {
      return undefined;
    }

    if (typeof IntersectionObserver === "undefined") {
      setVisibleServiceRows(
        Object.fromEntries(serviceRows.map((_, index) => [index, true])),
      );
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        setVisibleServiceRows((current) => {
          let hasChanged = false;
          const next = { ...current };

          entries.forEach((entry) => {
            const index = Number(entry.target.dataset.serviceIndex);

            if (Number.isNaN(index) || next[index] === entry.isIntersecting) {
              return;
            }

            next[index] = entry.isIntersecting;
            hasChanged = true;
          });

          return hasChanged ? next : current;
        });
      },
      {
        threshold: 0.25,
        rootMargin: "0px 0px -10% 0px",
      },
    );

    serviceRows.forEach((row) => observer.observe(row));

    return () => observer.disconnect();
  }, []);

  return (
    <PageShell pageClass="page-home">
      <section className="hero">
        <div className="hero-media" aria-hidden="true">
          <video
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            poster="/assets/image/profile-image.jpg"
          >
            <source src="/assets/image/myy web.mp4" type="video/mp4" />
          </video>
        </div>

        <div className="hero-overlay">
          <div className="hero-copy hero-copy-left">
            <p className="hero-kicker">Designing</p>
            <p className="hero-word">brands</p>
          </div>

          <div className="hero-copy hero-copy-right">
            <p className="hero-side-heading">
              <span className="hero-kicker">for the</span>
              <span className="hero-ai">AI</span>
              <span className="hero-era">era...</span>
            </p>
            <Link
              to="/services"
              className="mt-6 inline-flex items-center gap-3 self-end rounded-full border border-white/40 bg-black/15 px-5 py-2.5 font-display text-sm uppercase tracking-[0.14em] text-white backdrop-blur-sm transition duration-300 hover:-translate-y-0.5 hover:border-white hover:bg-black/25"
            >
              <span>See More</span>
              <span aria-hidden="true" className="text-base leading-none">
                &#8594;
              </span>
            </Link>
          </div>
        </div>
      </section>

      <div className="section">
        <section className="about-preview">
          <h2>About</h2>
          <p>
            I&apos;m Biswajit Nandi, a designer and inspiring filmmaker with{" "}
            <strong>5+ years of experience in UI/UX</strong>, branding, digital
            art, and retouching. I&apos;ve worked with Fotogafik Chandigarh, Lis Co
            Japan, and multiple brands as a freelancer, blending creativity
            with precision. Through my YouTube channel{" "}
            <strong>Natamandapa Creation</strong>, I share stories and short
            films that reflect my love for art and storytelling.
            {" "}
            <Link
              to="/about"
              className="ml-3 inline-block text-accent text-sm lowercase tracking-[0.08em] hover:underline"
            >
              see more
            </Link>
          </p>
        </section>

        <section className="work">
          <h2>Work with</h2>
          <WorkLogos animated />
        </section>

        <section>
          <h2 className="mb-5 font-display text-[28px] md:text-[35px]">
            Services
          </h2>
          <div className="flex flex-col gap-12">
            {homeServices.map((service, index) => {
              const isReverse = index === 1;
              const isVisible = Boolean(visibleServiceRows[index]);

              return (
                <article
                  data-service-index={index}
                  ref={(node) => {
                    serviceRowsRef.current[index] = node;
                  }}
                  className={`flex flex-col items-center justify-center gap-7 md:gap-[60px] ${
                    isReverse ? "md:flex-row-reverse" : "md:flex-row"
                  }`}
                  key={service.title}
                >
                  <div
                    className={`w-full max-w-[520px] ${getRevealClasses(
                      isVisible,
                      isReverse ? "right" : "left",
                    )}`}
                  >
                    <img
                      className="block w-full transition duration-300 ease-out hover:scale-[1.04] hover:shadow-[0_10px_28px_rgba(0,0,0,0.35)]"
                      src={service.image}
                      alt={service.title}
                    />
                  </div>
                  <div
                    className={`max-w-[420px] ${getRevealClasses(
                      isVisible,
                      isReverse ? "left" : "right",
                      true,
                    )} ${
                      isReverse
                        ? "text-center md:text-left"
                        : "text-center md:text-right"
                    }`}
                  >
                    <h3 className="mb-[14px] text-[2rem] leading-[1.1] md:text-[clamp(2.4rem,5vw,3.4rem)]">
                      {getServiceHeading(service)}
                    </h3>
                    <p className="leading-[1.8] text-[#ccc]">
                      {service.description}
                    </p>
                    <span className="text-[26px] text-[#3ea6ff]">&#8594;</span>
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <Link to="/services" className="btn-see">
          See More
        </Link>

        <section className="popular-services">
          <h2>Popular Services</h2>
          <div className="popular-grid">
            <div className="relative overflow-hidden">
              <img
                src="/assets/image/complete bundle start.jpg"
                alt="Popular service 1"
              />
              <Link
                to="/services"
                className="absolute bottom-8 left-5 inline-flex items-center justify-center rounded-none bg-[#d7ff1f] px-5 py-3 font-display text-lg font-medium text-accent shadow-[0_10px_18px_rgba(0,0,0,0.22)] transition duration-300 hover:-translate-y-0.5 hover:brightness-105 md:bottom-10 md:left-7 md:min-w-[140px] md:text-[1.55rem]"
              >
                Know More
              </Link>
            </div>
            <div className="relative overflow-hidden">
              <img
                src="/assets/image/complete bundle.jpg"
                alt="Popular service 2"
              />
              <Link
                to="/services"
                className="absolute bottom-8 left-5 inline-flex items-center justify-center rounded-none bg-[#d7ff1f] px-5 py-3 font-display text-lg font-medium text-accent shadow-[0_10px_18px_rgba(0,0,0,0.22)] transition duration-300 hover:-translate-y-0.5 hover:brightness-105 md:bottom-10 md:left-7 md:min-w-[140px] md:text-[1.55rem]"
              >
                Know More
              </Link>
            </div>
          </div>
        </section>

        <section className="portfolio-preview">
          <h2>Portfolio</h2>
          <div className="portfolio-preview-grid">
            <img src="/assets/image/Rectangle 10.png" alt="Portfolio preview 1" />
              <img
              src="/assets/image/vitamin-c-poster.jpg"
              alt="Portfolio preview 3"
            />
            <img src="/assets/image/fashion.jpg" alt="Portfolio preview 2" />
          
          </div>
        </section>

        <Link to="/portfolio" className="btn-see">
          See More
        </Link>
      </div>
    </PageShell>
  );
}
